/**
 * Background-immune timer utility.
 *
 * Why: Chrome/Firefox/Safari aggressively throttle `setInterval` and
 * `setTimeout` on hidden/background tabs (typically to ~1 Hz, and to
 * ~1 / min on mobile). The DJ Console's 500 ms progress poller and
 * 50 ms crossfade volume ramp both stop working under those rules,
 * which is exactly the "fails to load the new song / crossfade when
 * the app is in the background" bug.
 *
 * Web Worker timers are NOT throttled. We host a single shared
 * worker that fans tick events out to multiple subscribers by id,
 * and expose a `setInterval`-style API so the call site stays the
 * same as the original.
 *
 * Public API
 *   const handle = createBackgroundInterval(callback, intervalMs)
 *   handle.clear()
 *
 * The worker is created lazily on first use, lives for the lifetime
 * of the page (it is tiny — ~30 lines of code in a Blob URL — and
 * survives React StrictMode double-mounts cleanly).
 */

let _worker = null;
let _nextId = 1;
const _subscribers = new Map(); // id → callback

function _ensureWorker() {
  if (_worker) return _worker;

  const source = `
    const timers = {};
    self.onmessage = (e) => {
      const { type, id, interval } = e.data || {};
      if (type === 'start') {
        if (timers[id]) { clearInterval(timers[id]); }
        timers[id] = setInterval(() => { self.postMessage({ id }); }, interval);
      } else if (type === 'stop') {
        if (timers[id]) { clearInterval(timers[id]); delete timers[id]; }
      }
    };
  `;

  try {
    const blob = new Blob([source], { type: 'application/javascript' });
    _worker = new Worker(URL.createObjectURL(blob));
    _worker.onmessage = (e) => {
      const id = e.data && e.data.id;
      const cb = _subscribers.get(id);
      if (cb) cb();
    };
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.warn('[backgroundTimer] Web Worker unavailable, falling back to setInterval:', err);
    _worker = null;
  }
  return _worker;
}

export function createBackgroundInterval(callback, intervalMs) {
  const worker = _ensureWorker();

  // Fallback: no worker support → main-thread setInterval (will throttle
  // in background but at least keeps the app working in the foreground).
  if (!worker) {
    const handle = setInterval(callback, intervalMs);
    return { clear: () => clearInterval(handle) };
  }

  const id = _nextId++;
  _subscribers.set(id, callback);
  worker.postMessage({ type: 'start', id, interval: intervalMs });

  return {
    clear: () => {
      _subscribers.delete(id);
      worker.postMessage({ type: 'stop', id });
    },
  };
}
